import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Plus, Ban, Download, FileText, Sheet as SheetIcon, PenLine,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatDate, formatDateTime } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import { StatusChip } from "@/components/common/StatusChip";
import { ALLOWED_TRANSITIONS, projectStatusTone, transitionLabelKey } from "@/lib/projects";
import { PageHeader } from "@/components/common/PageHeader";
import { Section } from "@/features/workorders/Section";
import { ProgressBar } from "@/features/projects/ProgressBar";
import { ProjectDialog } from "@/features/projects/ProjectDialog";
import { ContratistaDialog } from "@/features/projects/ContratistaDialog";
import { MovementDialog } from "@/features/projects/MovementDialog";
import { CertificationDialog } from "@/features/projects/CertificationDialog";
import { ProgressDialog } from "@/features/projects/ProgressDialog";
import { ProjectTransitionDialog } from "@/features/projects/ProjectTransitionDialog";
import { Gallery } from "@/features/projects/Gallery";
import { PaymentDialog } from "@/features/finance/PaymentDialog";
import { VoidDialog } from "@/features/finance/VoidDialog";
import { PurchaseDialog } from "@/features/finance/PurchaseDialog";
import { Button } from "@/components/ui/button";

const Row = ({ label, value, testId }) => (
  <div className="flex items-start gap-3 border-b border-line py-2 last:border-0">
    <span className="w-[136px] shrink-0 font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{label}</span>
    <span data-testid={testId} className="min-w-0 flex-1 text-[12.5px] text-ink">{value ?? "—"}</span>
  </div>
);

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { t } = useT();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [transition, setTransition] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [contratistaDialog, setContratistaDialog] = useState(null);
  const [movementOpen, setMovementOpen] = useState(false);
  const [certDialog, setCertDialog] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["project-detail", id],
    queryFn: async () => (await api.get(`/projects/${id}`)).data,
  });

  const canSeePurchases = can("finance", "read");
  const { data: purchasesData } = useQuery({
    queryKey: ["purchases", { project_id: id }],
    queryFn: async () => (await api.get("/purchases", { params: { project_id: id, page_size: 100 } })).data,
    enabled: canSeePurchases,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6" data-testid="project-detail-loading">
        <div className="loading-bar" />
        <div className="mt-5 space-y-2.5">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[13px] w-full bg-surface-sunken" />)}
        </div>
      </div>
    );
  }

  const { proyecto: p, contratistas, cuenta, movimientos, avances, certificaciones } = data;
  const canUpdate = can("projects", "update");
  const canExport = can("projects", "export");
  const showMoney = cuenta && cuenta.monto_contratado_cents !== undefined;
  const transiciones = ALLOWED_TRANSITIONS[p.estado] || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["project-detail", id] });
    qc.invalidateQueries({ queryKey: ["projects"] });
    qc.invalidateQueries({ queryKey: ["purchases"] });
  };

  const exportAccount = async (fmt) => {
    const res = await api.get(`/projects/${id}/account.${fmt}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${p.code}_cuentas.${fmt}`; a.click();
    URL.revokeObjectURL(url);
  };

  const certsBySub = (subId) => (certificaciones || []).filter((c) => c.sub_id === subId && !c.es_contraasiento);

  return (
    <>
      <PageHeader
        testId="project-detail-header"
        breadcrumb={
          <button data-testid="project-back" onClick={() => navigate("/proyectos")}
            className="mb-1.5 inline-flex items-center gap-1 text-[12px] text-ink-secondary hover:text-ink">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("projects.detail.back")}
          </button>
        }
        title={
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono tabular-nums">{p.code}</span>
            <StatusChip tone={projectStatusTone(p.estado)} label={t(`projects.status.${p.estado}`)} testId="project-detail-status" />
          </span>
        }
        subtitle={<>{p.direccion_obra?.calle} · {p.direccion_obra?.ciudad}</>}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canUpdate && (
              <Button variant="outline" data-testid="project-edit-button" onClick={() => setEditOpen(true)} className="h-8">
                <PenLine className="mr-1.5 h-3.5 w-3.5" /> {t("app.edit")}
              </Button>
            )}
            {canUpdate && transiciones.map((destino) => (
              <Button
                key={destino}
                variant={destino === "cancelado" ? "outline" : "default"}
                data-testid={`project-action-${destino}`}
                onClick={() => setTransition(destino)}
                className={destino === "cancelado" ? "h-8 border-state-critical text-state-critical hover:bg-state-critical-soft" : "h-8 bg-ink text-paper hover:opacity-90"}
              >
                {t(`projects.transition.${transitionLabelKey(p.estado, destino)}`)}
              </Button>
            ))}
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto" data-testid="project-detail-page">
        <div className="grid divide-y divide-line xl:grid-cols-3 xl:divide-x xl:divide-y-0">
          {/* Zona 1: datos y contratistas */}
          <div className="space-y-8 px-5 py-5">
            <Section title={t("projects.detail.data")}>
              <Row label={t("projects.brand")} value={<span className="uppercase">{p.brand}</span>} />
              <Row label={t("projects.client")} value={p.cliente_tipo === "company" ? p.company_nombre : p.cliente_nombre} testId="project-detail-client" />
              {p.cliente_telefono && <Row label={t("projects.form.client_phone")} value={p.cliente_telefono} />}
              {p.cliente_email && <Row label={t("projects.form.client_email")} value={p.cliente_email} />}
              <Row label={t("app.address")} value={`${p.direccion_obra?.calle}, ${p.direccion_obra?.ciudad}, ${p.direccion_obra?.estado} ${p.direccion_obra?.zip || ""}`} />
              <Row label={t("projects.form.scope")} value={p.alcance} />
              <Row label={t("projects.form.start_date")} value={formatDate(p.fecha_inicio)} />
              <Row label={t("projects.form.end_date")} value={formatDate(p.fecha_fin_estimada)} />
              <Row label={t("projects.responsible")} value={p.responsable_nombre} />
              <Row label={t("projects.detail.created_by")} value={`${p.created_by_nombre || "—"} · ${formatDateTime(p.created_at)}`} />
              {p.motivo_pausa && <Row label={t("projects.transition.pause_reason")} value={p.motivo_pausa} />}
              {p.motivo_cancelacion && <Row label={t("projects.transition.cancel_reason")} value={p.motivo_cancelacion} />}
              {p.notas && <Row label={t("projects.form.notes")} value={p.notas} />}
              <Row label={t("projects.progress_label")} value={<ProgressBar pct={p.avance_pct || 0} testId="project-detail-progress" />} />
            </Section>

            <Section
              title={t("projects.detail.contractors")}
              testId="project-contractors"
              action={canUpdate && (
                <Button size="sm" variant="outline" data-testid="project-add-contractor"
                  onClick={() => setContratistaDialog({ mode: "assign" })} className="h-7 px-2 text-[12px]">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("projects.detail.add_contractor")}
                </Button>
              )}
            >
              {!contratistas.length ? (
                <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("projects.detail.no_contractors")}</p>
              ) : (
                <div className="space-y-3">
                  {contratistas.map((c) => (
                    <div key={c.sub_id} data-testid={`contratista-card-${c.sub_id}`} className="border border-line px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[13px] font-medium text-ink">{c.sub_nombre}</p>
                        {canUpdate && (
                          <div className="flex items-center gap-2">
                            <button data-testid={`contratista-amend-${c.sub_id}`}
                              onClick={() => setContratistaDialog({ mode: "amend", contratista: c })}
                              className="text-[11.5px] font-medium text-brand hover:text-brand-hover">
                              {t("projects.detail.amend")}
                            </button>
                            {c.monto_acordado_cents !== undefined && (
                              <button data-testid={`contratista-certify-${c.sub_id}`}
                                onClick={() => setCertDialog(true)}
                                className="text-[11.5px] font-medium text-brand hover:text-brand-hover">
                                {t("projects.detail.certify")}
                              </button>
                            )}
                            <button data-testid={`contratista-pay-${c.sub_id}`}
                              onClick={() => setPayTarget(c.sub_id)}
                              className="text-[11.5px] font-medium text-brand hover:text-brand-hover">
                              {t("projects.detail.pay")}
                            </button>
                          </div>
                        )}
                      </div>
                      {c.alcance && <p className="mt-0.5 text-[12px] text-ink-secondary">{c.alcance}</p>}
                      {c.monto_acordado_cents !== undefined && (
                        <div className="mt-2 grid grid-cols-4 gap-2 border-t border-line pt-2 text-[11.5px]">
                          <div>
                            <p className="text-ink-tertiary">{t("projects.detail.agreed_amount")}</p>
                            <Amount cents={c.monto_acordado_cents} className="text-[12px]" />
                          </div>
                          <div>
                            <p className="text-ink-tertiary">{t("projects.certifications.title")}</p>
                            <Amount cents={c.devengado_cents} className="text-[12px]" />
                          </div>
                          <div>
                            <p className="text-ink-tertiary">{t("projects.detail.paid")}</p>
                            <Amount cents={c.pagado_cents} className="text-[12px]" />
                          </div>
                          <div>
                            <p className="text-ink-tertiary">{t("projects.detail.balance")}</p>
                            <Amount cents={c.saldo_cents} className={c.saldo_cents < 0 ? "text-state-critical text-[12px]" : "text-[12px]"} />
                          </div>
                        </div>
                      )}
                      {!!certsBySub(c.sub_id).length && (
                        <div className="mt-2 border-t border-line pt-2">
                          <p className="pb-1 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-tertiary">{t("projects.certifications.title")}</p>
                          <ul className="space-y-1">
                            {certsBySub(c.sub_id).slice(0, 5).map((cert) => (
                              <li key={cert.id} className="flex items-center justify-between gap-2 text-[11.5px]" data-testid={`certification-row-${cert.id}`}>
                                <span className="truncate text-ink-secondary">{formatDate(cert.fecha)} · {cert.descripcion || "—"}</span>
                                <span className="flex items-center gap-2 shrink-0">
                                  <Amount cents={cert.monto_certificado_cents} className="text-[11.5px]" />
                                  {canUpdate && !cert.anulado && (
                                    <button data-testid={`certification-void-${cert.id}`}
                                      onClick={() => setVoidTarget({ kind: "project_certification", id: cert.id })}
                                      className="text-ink-tertiary hover:text-state-critical"><Ban className="h-3 w-3" /></button>
                                  )}
                                  {cert.anulado && <StatusChip tone="red" label={t("finance.payments.voided")} />}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Zona 2: cuentas de la obra */}
          <div className="space-y-8 px-5 py-5">
            <Section
              title={t("projects.detail.account")}
              testId="project-account"
              action={canExport && showMoney && (
                <div className="flex items-center gap-2">
                  <button data-testid="project-export-pdf" onClick={() => exportAccount("pdf")}
                    className="inline-flex items-center gap-1 text-[11.5px] font-medium text-ink-secondary hover:text-ink">
                    <FileText className="h-3.5 w-3.5" /> {t("projects.account.export_pdf")}
                  </button>
                  <button data-testid="project-export-xlsx" onClick={() => exportAccount("xlsx")}
                    className="inline-flex items-center gap-1 text-[11.5px] font-medium text-ink-secondary hover:text-ink">
                    <SheetIcon className="h-3.5 w-3.5" /> {t("projects.account.export_xlsx")}
                  </button>
                </div>
              )}
            >
              {!showMoney ? (
                <p className="py-4 text-center text-[12.5px] text-ink-tertiary">—</p>
              ) : (
                <>
                  <Row label={t("projects.account.contracted")} value={<Amount cents={cuenta.monto_contratado_cents} testId="account-contracted" className="text-[15px]" />} />
                  <Row label={t("projects.account.advances")} value={<Amount cents={cuenta.anticipos_cents} testId="account-advances" className="text-[15px]" />} />
                  <Row label={t("projects.account.expenses")} value={<Amount cents={cuenta.gastos_cents} testId="account-expenses" className="text-[15px]" />} />
                  {cuenta.pagado_contratistas_cents !== undefined && (
                    <Row label={t("projects.account.paid_contractors")} value={<Amount cents={cuenta.pagado_contratistas_cents} testId="account-paid-contractors" className="text-[15px]" />} />
                  )}
                  <Row label={t("projects.account.receivable_balance")} value={
                    <Amount cents={cuenta.saldo_por_cobrar_cents} testId="account-receivable"
                      className={(cuenta.saldo_por_cobrar_cents > 0 ? "text-state-warn" : "text-state-ok") + " text-[15px]"} />
                  } />
                  {cuenta.saldo_por_pagar_subs_cents !== undefined && (
                    <Row label={t("projects.account.payable_balance")} value={
                      <Amount cents={cuenta.saldo_por_pagar_subs_cents} testId="account-payable"
                        className={(cuenta.saldo_por_pagar_subs_cents > 0 ? "text-state-warn" : "text-state-ok") + " text-[15px]"} />
                    } />
                  )}
                </>
              )}
            </Section>

            <Section
              title={t("projects.movements.title")}
              testId="project-movements"
              action={canUpdate && (
                <Button size="sm" variant="outline" data-testid="project-add-movement" onClick={() => setMovementOpen(true)} className="h-7 px-2 text-[12px]">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("projects.movements.add")}
                </Button>
              )}
            >
              {!movimientos.length ? (
                <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("projects.movements.empty")}</p>
              ) : (
                <table className="w-full text-[12px]" data-testid="movements-table">
                  <tbody>
                    {movimientos.map((m) => (
                      <tr key={m.id} className="border-b border-line" data-testid={`movement-row-${m.id}`}>
                        <td className="py-1.5">{formatDate(m.fecha)}</td>
                        <td className="py-1.5">
                          <span className="inline-flex items-center gap-1">
                            {m.es_contraasiento && <StatusChip tone="amber" label={t("finance.payments.reversal")} />}
                            {t(`projects.movements.${m.tipo === "anticipo" ? "advance" : "expense"}`)}
                          </span>
                        </td>
                        <td className="truncate py-1.5 text-ink-secondary">{m.proveedor || m.descripcion || "—"}</td>
                        {m.monto_cents !== undefined && (
                          <td className="py-1.5 text-right"><Amount cents={m.monto_cents} className="text-[12px]" /></td>
                        )}
                        <td className="py-1.5 text-right">
                          {m.recibo && (
                            <a href={m.recibo.url} target="_blank" rel="noreferrer" data-testid={`movement-receipt-${m.id}`}
                              className="text-ink-tertiary hover:text-ink"><Download className="inline h-3.5 w-3.5" /></a>
                          )}
                          {canUpdate && !m.anulado && !m.es_contraasiento && (
                            <button data-testid={`movement-void-${m.id}`}
                              onClick={() => setVoidTarget({ kind: "project_movement", id: m.id })}
                              className="ml-2 text-ink-tertiary hover:text-state-critical"><Ban className="inline h-3.5 w-3.5" /></button>
                          )}
                          {m.anulado && <StatusChip tone="red" label={t("finance.payments.voided")} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {canSeePurchases && (
              <Section
                title={t("finance.purchases.detail_block.title")}
                testId="project-purchases"
                action={can("finance", "create") && (
                  <Button size="sm" variant="outline" data-testid="project-add-purchase" onClick={() => setPurchaseOpen(true)} className="h-7 px-2 text-[12px]">
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("finance.purchases.new")}
                  </Button>
                )}
              >
                {!purchasesData?.items?.length ? (
                  <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("wo.detail.no_purchases")}</p>
                ) : (
                  <ul className="space-y-1.5">
                    {purchasesData.items.map((p) => (
                      <li key={p.id} data-testid={`project-purchase-row-${p.id}`}
                        className="flex items-center justify-between gap-2 border-b border-line py-1.5 text-[12px]">
                        <span className="truncate text-ink-secondary">
                          {formatDate(p.fecha)} · {p.proveedor || p.descripcion || "—"}
                          {p.anulado && <span className="ml-1.5 text-state-critical">({t("finance.payments.voided")})</span>}
                        </span>
                        <Amount cents={p.monto_cents} className="text-[12px]" />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )}
          </div>

          {/* Zona 3: avances y galería */}
          <div className="space-y-8 px-5 py-5">
            <Section
              title={t("projects.progress.title")}
              testId="project-progress-section"
              action={canUpdate && (
                <Button size="sm" variant="outline" data-testid="project-add-progress" onClick={() => setProgressOpen(true)} className="h-7 px-2 text-[12px]">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("projects.progress.add")}
                </Button>
              )}
            >
              {!avances.length ? (
                <p className="py-4 text-center text-[12.5px] text-ink-tertiary">{t("projects.progress.empty")}</p>
              ) : (
                <ul className="space-y-3">
                  {avances.map((a) => (
                    <li key={a.id} data-testid={`progress-row-${a.id}`} className="border-l-2 border-line-strong pl-3">
                      <p className="flex items-center gap-2 text-[12px] text-ink-tertiary">
                        {formatDate(a.fecha)} <StatusChip tone="gray" label={`${a.avance_pct}%`} />
                      </p>
                      <p className="text-[12.5px] text-ink">{a.descripcion}</p>
                      {!!(a.fotos || []).length && (
                        <div className="mt-1.5 flex gap-1.5">
                          {a.fotos.map((f, i) => (
                            <img key={i} src={f.thumb_url || f.url} alt="" className="h-12 w-12 border border-line object-cover" />
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t("projects.gallery.title")} testId="project-gallery-section">
              <Gallery projectId={id} code={p.code} avances={avances} movimientos={movimientos} />
            </Section>
          </div>
        </div>
      </div>

      <ProjectDialog open={editOpen} onOpenChange={setEditOpen} project={p} />
      <ContratistaDialog
        open={!!contratistaDialog} onOpenChange={(v) => !v && setContratistaDialog(null)}
        projectId={id} contratista={contratistaDialog?.contratista} existentes={contratistas}
      />
      <MovementDialog open={movementOpen} onOpenChange={setMovementOpen} projectId={id} />
      <PurchaseDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} projectId={id} invalidateKeys={[["purchases", { project_id: id }], ["project-detail", id]]} />
      <CertificationDialog open={!!certDialog} onOpenChange={(v) => !v && setCertDialog(null)} projectId={id} contratistas={contratistas} />
      <ProgressDialog open={progressOpen} onOpenChange={setProgressOpen} projectId={id} />
      <ProjectTransitionDialog projectId={id} origen={p.estado} destino={transition} onOpenChange={(v) => !v && setTransition(null)} onDone={invalidate} />
      <PaymentDialog open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)} kind="payment" subId={payTarget} projectId={id} />
      {voidTarget && (
        <VoidDialog open={Boolean(voidTarget)} onOpenChange={(v) => !v && setVoidTarget(null)} kind={voidTarget.kind} id={voidTarget.id}
          invalidateKeys={[["project-detail", id]]} />
      )}
    </>
  );
}
